// import { useLogin, useRegister } from '@/lib/api/mutations/useAuthMutations';
// import { useAuth as useAuthQuery } from '@/lib/api/queries/useAuth';
// import React, { createContext, useContext, useState } from 'react';
// import { LoginCredentials, RegisterCredentials, User } from '../types/auth';
// import { Role, getPermissionsForRole } from './RBACContext';

// interface AuthContextType {
//   user: User | undefined;
//   token: string | null;
//   login: (credentials: LoginCredentials) => Promise<void>;
//   register: (credentials: RegisterCredentials) => Promise<void>;
//   logout: () => void;
//   isLoading: boolean;
//   error: string | null;
//   userRole: Role;
//   permissions: ReturnType<typeof getPermissionsForRole>;
// }

// const AuthContext = createContext<AuthContextType | null>(null);

// export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
//   children,
// }) => {
//   const [token, setToken] = useState<string | null>(() =>
//     localStorage.getItem('token')
//   );
//   const [error, setError] = useState<string | null>(null);

//   const { data: user, isLoading, refetch } = useAuthQuery();
//   const loginMutation = useLogin();
//   const registerMutation = useRegister();

//   // Get user role from user data or default to EXECUTIVE
//   const userRole: Role = user?.role || 'EXECUTIVE';
//   const permissions = getPermissionsForRole(userRole);

//   const login = async (credentials: LoginCredentials) => {
//     try {
//       setError(null);
//       const response = await loginMutation.mutateAsync(credentials);
//       refetch();
//       localStorage.setItem('token', response.access_token);
//       setToken(response.access_token);
//     } catch (error: any) {
//       setError(error.response?.data?.message || 'Login failed');
//       throw error;
//     }
//   };

//   const register = async (credentials: RegisterCredentials) => {
//     try {
//       setError(null);
//       await registerMutation.mutateAsync(credentials);
//       await login({
//         username: credentials.username,
//         password: credentials.password,
//       });
//     } catch (error: any) {
//       setError(error.response?.data?.message || 'Registration failed');
//       throw error;
//     }
//   };

//   const logout = () => {
//     localStorage.removeItem('token');
//     sessionStorage.removeItem('sellco_onboarding_dismissed');
//     setToken(null);
//     window.location.reload();
//   };

//   return (
//     <AuthContext.Provider
//       value={{
//         user,
//         token,
//         login,
//         register,
//         logout,
//         isLoading,
//         error,
//         userRole,
//         permissions,
//       }}
//     >
//       {children}
//     </AuthContext.Provider>
//   );
// };

// export const useAuth = () => {
//   const context = useContext(AuthContext);
//   if (!context) {
//     throw new Error('useAuth must be used within an AuthProvider');
//   }
//   return context;
// };

import { useLogin, useRegister } from '@/lib/api/mutations/useAuthMutations';
import { useAuth as useAuthQuery } from '@/lib/api/queries/useAuth';
import { AccessControlStore } from '@/lib/api/accessControl';
import React, { createContext, useContext, useState } from 'react';
import { LoginCredentials, RegisterCredentials, User } from '../types/auth';
import { Role, getPermissionsForRole } from './RBACContext';
interface AuthContextType {
  user: User | undefined;
  token: string | null;
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (credentials: RegisterCredentials) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  error: string | null;
  userRole: Role;
  permissions: ReturnType<typeof getPermissionsForRole>;
  isAuthenticated: boolean; // Added
  userEmail: string; // Added
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [token, setToken] = useState<string | null>(() =>
    localStorage.getItem('token')
  );
  const [error, setError] = useState<string | null>(null);

  const { data: user, isLoading, refetch } = useAuthQuery();
  const loginMutation = useLogin();
  const registerMutation = useRegister();

  // Get user role from user data or default to EXECUTIVE
  const userRole: Role = user?.role || 'EXECUTIVE';
  const userEmail: string = user?.email || '';
  const permissions = getPermissionsForRole(userRole);
  const isAuthenticated = !!token; // Compute isAuthenticated

  const login = async (credentials: LoginCredentials) => {
    try {
      setError(null);
      const response = await loginMutation.mutateAsync(credentials);
      refetch();
      localStorage.setItem('token', response.access_token);
      setToken(response.access_token);
      localStorage.removeItem('login-detail');
    } catch (error: any) {
      setError(error.response?.data?.message || 'Login failed');
      throw error;
    }
  };

  const register = async (credentials: RegisterCredentials) => {
    try {
      setError(null);
      await registerMutation.mutateAsync(credentials);
      await login({
        email: credentials.username,
        password: credentials.password,
      });
    } catch (error: any) {
      setError(error.response?.data?.message || 'Registration failed');
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('marketingstream_profile_link_dismissed'); // Reset modal dismissal state
    localStorage.removeItem('sellco_onboarding_dismissed');
    sessionStorage.removeItem('showFeedback');
    localStorage.removeItem('noteHidden'); // Reset note visibility state
    localStorage.removeItem('upload_asin_dismissed');
    localStorage.removeItem('app_v2_ads_profiles');
    localStorage.removeItem('app_v2_header2_profile');
    localStorage.removeItem('app_v2_nonads_profiles');
    localStorage.removeItem('app_v2_prefs_country');
    localStorage.removeItem('app_v2_prefs_dates');
    localStorage.removeItem('app_v2_snap_profiles');
    localStorage.removeItem('sellco_onboarding_dismissed');
    AccessControlStore.clearDeniedContext();
    setToken(null);
    // A reload would keep the user on public pages like /suspended, so go to login explicitly.
    window.location.replace('/login');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        register,
        logout,
        isLoading,
        error,
        userRole,
        permissions,
        isAuthenticated, // Added
        userEmail,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
